import { authFetch } from '../lib/authFetch';
import { buildQueryString } from '../lib/query';
import { isDevEnvironment } from '../lib/env';
import { resolveBase, json } from './client';
import { dedupeRequest } from '../lib/requestDeduplication';
import {
  generatedUsersSchema,
  isAllowedPageValue,
  isUsersFieldType,
  isUsersStandardFieldKey,
} from '../types/generated/users-schema';
import type {
  AllowedPageOption,
  AllowedPageValue,
  UsersFieldType,
  UsersSchemaDefinition,
  UsersStandardFieldDefinition,
} from '../types/generated/users-schema';

export interface UserRecord {
  id: string;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  allowed_pages?: AllowedPageValue[];
  [key: string]: unknown;
}

/** Get user display name with standard fallback chain */
export function getUserDisplayName(user: UserRecord): string {
  return (
    user.display_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.username ||
    user.email
  );
}

export interface UsersListResponse {
  users: UserRecord[];
}

type UsersSchemaApiResponse = {
  allowed_pages?: unknown[];
  standard_fields?: unknown[];
  allowed_custom_field_types?: unknown[];
};

const USERS_CACHE_TTL_MS = 5 * 60 * 1000;
const usersCache = new Map<string, { timestamp: number; data: UserRecord[] }>();

export async function listUsers(apiBase: string, search?: string): Promise<UserRecord[]> {
  const base = resolveBase(apiBase);
  const query = buildQueryString(
    {
      q: search ? search : undefined,
    },
    { includeQuestionMark: true },
  );

  const url = `${base}/api/users/list${query}`;
  const cacheKey = url;
  const cached = usersCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < USERS_CACHE_TTL_MS) {
    return cached.data;
  }

  const fetchUsers = async () => {
    const response = await authFetch(url);
    const data = await json<UsersListResponse>(response);
    if (data.users && Array.isArray(data.users)) {
      return data.users as UserRecord[];
    }
    return [] as UserRecord[];
  };

  const users = await dedupeRequest(url, fetchUsers);
  usersCache.set(cacheKey, { timestamp: Date.now(), data: users });
  return users;
}

function isPresent<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function toAllowedPageOption(raw: unknown): AllowedPageOption | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const valueCandidate =
    typeof candidate.value === 'string'
      ? candidate.value
      : typeof candidate.key === 'string'
        ? candidate.key
        : null;
  if (!valueCandidate || !isAllowedPageValue(valueCandidate)) {
    return null;
  }
  const label = typeof candidate.label === 'string' ? candidate.label : valueCandidate;
  const description = typeof candidate.description === 'string' ? candidate.description : undefined;
  return { value: valueCandidate, label, description };
}

function toStandardFieldDefinition(raw: unknown): UsersStandardFieldDefinition | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const keyCandidate = typeof candidate.key === 'string' ? candidate.key : null;
  if (!keyCandidate || !isUsersStandardFieldKey(keyCandidate)) {
    return null;
  }
  const typeCandidate = typeof candidate.type === 'string' ? candidate.type : null;
  if (!typeCandidate || !isUsersFieldType(typeCandidate)) {
    return null;
  }

  const definition: UsersStandardFieldDefinition = {
    key: keyCandidate,
    label: typeof candidate.label === 'string' ? candidate.label : keyCandidate,
    type: typeCandidate,
    required: Boolean(candidate.required),
  };

  if (Object.prototype.hasOwnProperty.call(candidate, 'default')) {
    definition.default = candidate.default;
  }
  if (typeof candidate.description === 'string') {
    definition.description = candidate.description;
  }
  if (Array.isArray(candidate.options)) {
    definition.options = [...candidate.options];
  }
  if (typeof candidate.is_custom === 'boolean') {
    definition.is_custom = candidate.is_custom;
  }
  if (typeof candidate.order === 'number') {
    definition.order = candidate.order;
  }
  if (typeof candidate.id === 'string' && candidate.id) {
    definition.id = candidate.id;
  }

  return definition;
}

function cloneGeneratedUsersSchema(): UsersSchemaDefinition {
  const standardFields = generatedUsersSchema.standard_fields.map((field) => {
    const cloned: UsersStandardFieldDefinition = {
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      default: field.default,
      description: field.description,
    };
    if (Array.isArray(field.options)) {
      cloned.options = [...field.options];
    }
    if (typeof field.is_custom === 'boolean') {
      cloned.is_custom = field.is_custom;
    }
    if (typeof field.order === 'number') {
      cloned.order = field.order;
    }
    if (typeof field.id === 'string' && field.id) {
      cloned.id = field.id;
    }
    return cloned;
  });

  const result: UsersSchemaDefinition = {
    allowed_pages: generatedUsersSchema.allowed_pages.map((option) => ({
      value: option.value,
      label: option.label,
      description: option.description,
    })),
    standard_fields: standardFields,
  };

  if (Array.isArray(generatedUsersSchema.allowed_custom_field_types)) {
    result.allowed_custom_field_types = [...generatedUsersSchema.allowed_custom_field_types];
  }

  return result;
}

export async function fetchUsersSchema(apiBase?: string): Promise<UsersSchemaDefinition> {
  const base = resolveBase(apiBase);
  try {
    const response = await authFetch(`${base}/api/users/schema`);
    const data = await json<UsersSchemaApiResponse>(response);
    const allowedPages = Array.isArray(data.allowed_pages)
      ? data.allowed_pages.map(toAllowedPageOption).filter(isPresent)
      : [];
    const standardFields = Array.isArray(data.standard_fields)
      ? data.standard_fields.map(toStandardFieldDefinition).filter(isPresent)
      : [];
    const customTypes = Array.isArray(data.allowed_custom_field_types)
      ? data.allowed_custom_field_types.filter(
          (type: unknown): type is UsersFieldType => typeof type === 'string' && isUsersFieldType(type)
        )
      : undefined;

    if (!allowedPages.length || !standardFields.length) {
      throw new Error('Incomplete users schema payload');
    }
    const result: UsersSchemaDefinition = {
      allowed_pages: allowedPages,
      standard_fields: standardFields,
    };
    if (customTypes && customTypes.length > 0) {
      result.allowed_custom_field_types = customTypes;
    }
    return result;
  } catch (error) {
    if (isDevEnvironment()) {
      // eslint-disable-next-line no-console
      console.warn('[fetchUsersSchema] Falling back to generated schema data.', error);
    }
    return cloneGeneratedUsersSchema();
  }
}

/**
 * CompanyCam user ID to app user mapping.
 * Used to display correct user names for CompanyCam comments.
 */
export interface CompanyCamUserMapping {
  [companycamUserId: string]: {
    id: string;
    display_name: string;
  };
}

const companycamMappingCache: { timestamp: number; data: CompanyCamUserMapping } | null = null;
const COMPANYCAM_MAPPING_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Fetch the mapping of CompanyCam user IDs to app user info.
 * Used by CompanyCam components to show app user names for comments.
 */
export async function fetchCompanyCamUserMapping(apiBase: string): Promise<CompanyCamUserMapping> {
  const base = resolveBase(apiBase);
  const url = `${base}/api/users/companycam-mapping`;

  // Check cache
  if (companycamMappingCache && Date.now() - companycamMappingCache.timestamp < COMPANYCAM_MAPPING_CACHE_TTL_MS) {
    return companycamMappingCache.data;
  }

  const fetchMapping = async () => {
    const response = await authFetch(url);
    const data = await json<{ mapping?: CompanyCamUserMapping }>(response);
    return data.mapping || {};
  };

  const mapping = await dedupeRequest(url, fetchMapping);
  // Update cache reference (can't mutate const, but we can update the module-level variable)
  (globalThis as unknown as { __ccMappingCache?: typeof companycamMappingCache }).__ccMappingCache = {
    timestamp: Date.now(),
    data: mapping,
  };
  return mapping;
}

/**
 * Trigger a sync of CompanyCam users to app users by email.
 * Admin only endpoint.
 */
export async function syncCompanyCamUsers(apiBase: string): Promise<{
  matched: number;
  total_app_users: number;
  total_companycam_users: number;
  unmatched_app_users: string[];
  unmatched_companycam_users: string[];
}> {
  const base = resolveBase(apiBase);
  const url = `${base}/api/users/sync-companycam`;

  const response = await authFetch(url, { method: 'POST' });
  const data = await json<{
    matched?: number;
    total_app_users?: number;
    total_companycam_users?: number;
    unmatched_app_users?: string[];
    unmatched_companycam_users?: string[];
  }>(response);

  return {
    matched: data.matched || 0,
    total_app_users: data.total_app_users || 0,
    total_companycam_users: data.total_companycam_users || 0,
    unmatched_app_users: data.unmatched_app_users || [],
    unmatched_companycam_users: data.unmatched_companycam_users || [],
  };
}





